import type { Task } from "@app/core";
import { Card, cn } from "@app/ui";
import { memo } from "react";

import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { formatDurationCompact } from "../../lib/format.js";
import { dayKeyOf, type DayLoad } from "./model.js";

interface Props {
  loads: DayLoad[];
  /** Selected day key (see `dayKeyOf`) — narrows the board + queue. */
  selected: string | null;
  onSelect: (dayKey: string | null) => void;
  /** Resolved accent for a task (matrix diagonal → flag → neutral). */
  colorOf: (task: Task) => string;
  /** Day currently hovered by a board-chip drag (release = schedule there). */
  dropDay: string | null;
  /** Day that just received a scheduled drop — briefly flash-highlighted. */
  flashDay: string | null;
  /** Daily planning capacity in hours; 0 = off (bar relative to the busiest day). */
  capacityHours: number;
  onCapacityChange: (hours: number) => void;
  /** Open the "Plan my day" proposal for the selected day. */
  onPlan: () => void;
}

const MAX_DOTS = 8;
const CAPACITY_CHOICES = [2, 4, 6, 8, 10, 12];

/**
 * Day-by-day load for the next two weeks: recurrence-expanded occurrences as
 * colored dots plus the summed time estimate, so overloaded days stand out
 * while deciding. Click a day to narrow the whole view to it; drop a board
 * chip on a day to schedule it there. With a daily capacity set, the load bar
 * fills against the capacity and turns amber near it / red over it.
 */
export const LoadStrip = memo(function LoadStrip({ loads, selected, onSelect, colorOf, dropDay, flashDay, capacityHours, onCapacityChange, onPlan }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const max = Math.max(1, ...loads.map((d) => d.minutes));
  const capMin = capacityHours * 60;

  return (
    <Card className="p-2">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1 px-1">
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("priorities.strip.title")}</h2>
        <span className="ml-auto flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
          {selected && (
            <>
              <button
                type="button"
                className="rounded-full border border-primary/50 px-2 py-0.5 text-xs font-medium text-primary transition hover:bg-primary/10 active:scale-95 motion-reduce:transform-none"
                onClick={onPlan}
              >
                {t("priorities.plan.button")}
              </button>
              <button type="button" className="text-xs text-primary hover:underline" onClick={() => onSelect(null)}>
                {t("priorities.strip.clear")}
              </button>
            </>
          )}
          <select
            value={capacityHours}
            onChange={(e) => onCapacityChange(Number(e.target.value))}
            title={t("priorities.strip.capacity")}
            aria-label={t("priorities.strip.capacity")}
            className="rounded border bg-background px-1 py-0.5 text-[calc(11px*var(--app-text-scale))] text-muted-foreground"
          >
            <option value={0}>{t("priorities.strip.capacityOff")}</option>
            {CAPACITY_CHOICES.map((h) => (
              <option key={h} value={h}>{h} h</option>
            ))}
          </select>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:grid-cols-[repeat(14,minmax(0,1fr))]">
        {loads.map((day, i) => {
          const key = dayKeyOf(day.date);
          const isSelected = selected === key;
          const isToday = i === 0;
          const isDropTarget = dropDay === key;
          const shown = day.tasks.slice(0, MAX_DOTS);
          const rest = day.tasks.length - shown.length;
          const over = capMin > 0 && day.minutes > capMin;
          const near = capMin > 0 && !over && day.minutes >= 0.8 * capMin;
          const frac = capMin > 0 ? day.minutes / capMin : day.minutes / max;
          return (
            <button
              key={key}
              type="button"
              data-strip-day={key}
              onClick={() => onSelect(isSelected ? null : key)}
              aria-pressed={isSelected}
              className={cn(
                "flex min-w-0 flex-col items-center gap-1 rounded-md border px-0.5 py-1.5 transition-all duration-150 hover:bg-accent/40 active:scale-95 motion-reduce:transition-none motion-reduce:transform-none",
                isSelected ? "border-primary bg-primary/10" : "border-transparent",
                isToday && !isSelected && "border-border bg-muted/30",
                isDropTarget && "scale-[1.08] border-primary ring-2 ring-primary/60",
                flashDay === key && "flash-highlight",
              )}
              title={`${day.tasks.length} · ${formatDurationCompact(day.minutes)}${capMin > 0 ? ` / ${capacityHours} h` : ""}`}
            >
              <span className="text-[calc(11px*var(--app-text-scale))] font-medium leading-none text-muted-foreground">
                {day.date.toLocaleDateString(locale, { weekday: "short" })}
              </span>
              <span className={cn("text-xs font-semibold leading-none", isToday && "text-primary")}>
                {day.date.getDate()}
              </span>
              <span className="flex min-h-[0.75rem] max-w-full flex-wrap items-center justify-center gap-[3px] px-0.5">
                {shown.map((task, j) => (
                  <span key={`${task.id}-${j}`} className="h-1.5 w-1.5 rounded-full" style={{ background: colorOf(task) }} />
                ))}
                {rest > 0 && <span className="text-[calc(10px*var(--app-text-scale))] leading-none text-muted-foreground">+{rest}</span>}
              </span>
              {/* Relative load bar — the at-a-glance "how full is this day". */}
              <span className="h-1 w-full max-w-[2rem] overflow-hidden rounded-full bg-border/60">
                <span
                  className={cn(
                    "block h-full rounded-full transition-[width] duration-300 ease-out motion-reduce:transition-none",
                    over ? "bg-red-500/80" : near ? "bg-amber-500/70" : "bg-primary/60",
                  )}
                  style={{ width: `${Math.min(100, Math.round(frac * 100))}%` }}
                />
              </span>
              <span className={cn("text-[calc(10px*var(--app-text-scale))] leading-none", over ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground")}>
                {day.minutes > 0 ? formatDurationCompact(day.minutes) : "·"}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
});
